import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/database.generated';

export type JournalTemplateRow = Database['public']['Tables']['journal_templates']['Row'];
export type ClinicType = Database['public']['Enums']['clinic_type'];

// ---- Query keys ----

export const journalTemplatesKey = (
  clinicId: string | null | undefined,
  patientId?: string | null,
) => ['journal', 'templates', clinicId ?? null, patientId ?? null] as const;

// ---- Queries ----

/**
 * Fetch journal templates for a clinic.
 *
 * - Without patientId: returns only clinic-wide templates (patient_id IS NULL).
 *   Used by the Settings page.
 * - With patientId: returns clinic-wide templates AND patient-specific templates
 *   for that patient. Used by journal writing surfaces.
 */
export function useJournalTemplates(
  clinicId: string | null | undefined,
  patientId?: string | null,
) {
  return useQuery({
    queryKey: journalTemplatesKey(clinicId, patientId),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<JournalTemplateRow[]> => {
      if (!clinicId) return [];

      const baseQuery = supabase
        .from('journal_templates')
        .select('*')
        .eq('clinic_id', clinicId)
        .is('archived_at', null);

      const filteredQuery = patientId
        ? baseQuery.or(`patient_id.is.null,patient_id.eq.${patientId}`)
        : baseQuery.is('patient_id', null);

      const { data, error } = await filteredQuery.order('title', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 30_000,
  });
}

// ---- Mutations ----

export function useCreateJournalTemplate(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      title: string;
      content_md?: string;
      category?: string;
      vertical_type?: ClinicType | null;
      patient_id?: string | null;
    }) => {
      const { data, error } = await supabase.rpc('create_journal_template', {
        p_clinic_id: clinicId!,
        p_title: input.title,
        p_content_md: input.content_md ?? '',
        p_category: input.category ?? undefined,
        p_vertical_type: input.vertical_type ?? undefined,
        p_patient_id: input.patient_id ?? undefined,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // Invalidate all template queries for this clinic (covers both with/without patientId)
      if (clinicId) void qc.invalidateQueries({ queryKey: ['journal', 'templates', clinicId] });
    },
  });
}

export function useUpdateJournalTemplate(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      title?: string;
      content_md?: string;
      category?: string;
      vertical_type?: ClinicType | null;
    }) => {
      const { data, error } = await supabase.rpc('update_journal_template', {
        p_id: input.id,
        p_title: input.title ?? undefined,
        p_content_md: input.content_md ?? undefined,
        p_category: input.category ?? undefined,
        p_vertical_type: input.vertical_type ?? undefined,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      if (clinicId) void qc.invalidateQueries({ queryKey: ['journal', 'templates', clinicId] });
    },
  });
}

export function useArchiveJournalTemplate(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase.rpc('archive_journal_template', { p_id: id });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      if (clinicId) void qc.invalidateQueries({ queryKey: ['journal', 'templates', clinicId] });
    },
  });
}
